import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { FIRST_PAGE, getNextPageFromMeta } from '@/api/pagination';
import {
  moTravelRouteSchema,
  moTravelStatsSchema,
  paginatedMoTravelSchema,
  type MoTravelFilterParams,
  type MoTravelListParams,
  type MoTravelPeriodParams,
  type MoTravelRoute,
  type MoTravelStats,
  type PaginatedMoTravel,
} from '../types/moTravel.types';

const MO_TRAVEL_STALE_TIME = 2 * 60 * 1000;

export const moTravelQueryKeys = {
  all: () => ['mo-travel'] as const,
  lists: () => [...moTravelQueryKeys.all(), 'list'] as const,
  list: (params: MoTravelListParams) => [...moTravelQueryKeys.lists(), params] as const,
  stats: (params: MoTravelFilterParams) => [...moTravelQueryKeys.all(), 'stats', params] as const,
  routes: () => [...moTravelQueryKeys.all(), 'route'] as const,
  route: (userId: number, params: MoTravelPeriodParams) =>
    [...moTravelQueryKeys.routes(), userId, params] as const,
};

export const moTravelListInfiniteQueryOptions = (params: MoTravelListParams) =>
  infiniteQueryOptions({
    queryKey: moTravelQueryKeys.list(params),
    queryFn: async ({ pageParam }): Promise<PaginatedMoTravel> => {
      const res = await apiClient.get(API.moTravel.list, {
        params: { ...params, page: pageParam },
      });
      return paginatedMoTravelSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: MO_TRAVEL_STALE_TIME,
    refetchInterval: MO_TRAVEL_STALE_TIME,
  });

export const moTravelStatsQueryOptions = (params: MoTravelFilterParams) =>
  queryOptions({
    queryKey: moTravelQueryKeys.stats(params),
    queryFn: async (): Promise<MoTravelStats> => {
      const res = await apiClient.get(API.moTravel.summary, { params });
      return moTravelStatsSchema.parse(res.data);
    },
    staleTime: MO_TRAVEL_STALE_TIME,
    refetchInterval: MO_TRAVEL_STALE_TIME,
  });

export const moTravelRouteQueryOptions = (userId: number, params: MoTravelPeriodParams) =>
  queryOptions({
    queryKey: moTravelQueryKeys.route(userId, params),
    queryFn: async (): Promise<MoTravelRoute> => {
      const res = await apiClient.get(API.moTravel.route(userId), { params });
      return moTravelRouteSchema.parse(res.data);
    },
    staleTime: MO_TRAVEL_STALE_TIME,
  });
